import * as React from 'react';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { MenuBookOutlined } from '@mui/icons-material';
import * as Paths from '@toolkit/router/paths';
import PageNotFound from 'Common/components/PageNotFound';
import { useToolkitQuery } from 'Common/services/api';
import { delay } from 'lodash';
import { Location, useLocation } from 'react-router-dom';
import { ParseAsciidocWithRef } from 'src/scripts/content/general/updates/ReleaseNoteStyle';
import RenderAsciidoc from 'Common/components/RenderAsciidoc';
import { LoadingIndicator } from '@toolkit/common';

export default function ReleaseNoteWrapper(props: {
  year?: string;
}): ReactNode {
  const tocRefs = useRef({});
  const location: Location = useLocation();

  let docPath = `updates/release-notes-${props.year}.html`;
  if (props.year === 'latest') {
    docPath = `updates/release-notes.html`;
  }

  const { isLoading, data, error } = useToolkitQuery<string>(docPath, {
    transform: (response) => response.text(),
    baseUrl: '/toolkit',
  });

  const content = useMemo(() => {
    if (error) {
      return (
        <PageNotFound
          page="updates"
          path={Paths.Home}
          icon={<MenuBookOutlined />}
        />
      );
    } else if (data) {
      return <ParseAsciidocWithRef text={data} ref={tocRefs} />;
    } else {
      return <></>;
    }
  }, [error, data, tocRefs]);

  useEffect(() => {
    let selectedTOC = null;
    const route: string = location.hash.replace('#', '');
    if (content) {
      if (route && tocRefs[route]) {
        selectedTOC = route;
      } else {
        selectedTOC = Object.keys(tocRefs?.current)[0];
      }
      if (selectedTOC) {
        delay(() => {
          tocRefs[selectedTOC]?.scrollIntoView({
            behavior: 'smooth',
          });
        }, 300);
      }
    }
  }, [location.hash, data, content]);

  if (isLoading) {
    return (
      <LoadingIndicator fullScreen={true} title="Loading documentation..." />
    );
  }

  return <RenderAsciidoc template={content} />;
}
